'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { AppShell } from './app-shell';
import { PageTransition } from '@/components/shared/ui/page-transition';
import { OnboardingGuard } from '@/components/features/onboarding/onboarding-guard';
import { useAuth } from '@/lib/integrations/supabase';
import { VantaBackground } from '@/components/shared/ui/vanta-background';

// Routes that should NOT show the app shell (sidebar, assistant dock, etc.)
const AUTH_ROUTES = ['/', '/login', '/register', '/reset', '/reset-password', '/confirm-booking'];

// Check if pathname matches a queue route
function isQueueRoute(pathname: string): boolean {
  return pathname.startsWith('/q');
}

export function ConditionalAppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated, isLoading } = useAuth();

  // Calculate these values before any hooks to ensure consistent hook order
  const isQueue = isQueueRoute(pathname);
  const isAuthRoute = AUTH_ROUTES.includes(pathname);

  // Hide the app shell loader once React mounts
  useEffect(() => {
    // Hide the initial HTML loader
    if (typeof window !== 'undefined' && (window as any).__hideAppShellLoader) {
      (window as any).__hideAppShellLoader();
    }
  }, []);

  // For protected routes (not auth routes), check authentication
  // This hook is now always called in the same order
  useEffect(() => {
    if (!isQueue && !isAuthRoute && !isLoading && !isAuthenticated) {
      // User is not authenticated and trying to access protected route - redirect to login
      router.replace('/login');
    }
  }, [isQueue, isAuthRoute, isLoading, isAuthenticated, router]);

  // Queue routes (public, no shell, no background)
  if (isQueue) {
    return <>{children}</>;
  }

  if (!isAuthRoute) {
    // Show loading spinner while checking auth
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-[#FF6B35]"></div>
          <p className="text-sm text-muted-foreground animate-pulse">Loading your workspace...</p>
        </div>
      );
    }

    // Don't render protected content if not authenticated
    if (!isAuthenticated) {
      return null;
    }

    return (
      <OnboardingGuard>
        <AppShell>
          <PageTransition>{children}</PageTransition>
        </AppShell>
      </OnboardingGuard>
    );
  }

  // For confirmation page, render without any wrapper (no background, no shell)
  if (pathname === '/confirm-booking') {
    return <>{children}</>;
  }

  // For auth routes, wrap with video background
  return (
    <VantaBackground key="auth-background" className="flex items-center justify-center p-4">
      {children}
    </VantaBackground>
  );
}

export default ConditionalAppShell;
